export const enUs = {
  code: "en",
  metaTitle: "Murilo Lodovico — Full-Stack Software Engineer",
  metaDescription:
    "Full-stack software engineer in Campinas, Brazil. Java, TypeScript, React, Spring Boot, and modern architecture. Open to remote or hybrid roles.",
  skipToContent: "Skip to content",
  openMenu: "Open menu",
  closeMenu: "Close menu",
  langLabel: "Language",
  navAbout: "About",
  navExperience: "Experience",
  navSkills: "Stack",
  navProjects: "Projects",
  navEducation: "Education",
  navContact: "Contact",
  role: "Full-Stack Software Engineer",
  currentLabel: "Currently",
  currentCompany: "NAVA",
  locationLabel: "Location",
  location: "Campinas, Brazil",
  workModeLabel: "Work mode",
  workMode: "Remote or hybrid",
  heroLead:
    "I design and evolve digital products across front-end and back-end, with Java, TypeScript, and modern architecture. Focused on quality, performance, and systems that stay easy to maintain.",
  ctaExperience: "See experience",
  ctaResume: "Download resume",
  ctaContact: "Get in touch",
  resume: "Resume",
  statYears: "6+",
  statYearsLabel: "years building software",
  statStack: "Java + JS",
  statStackLabel: "from domain to UI",
  statWork: "Remote / hybrid",
  statWorkLabel: "Campinas metro area",
  aboutTitle: "About",
  aboutEyebrow: "Profile",
  aboutP1:
    "Software engineer with a solid track record shipping robust features, fixing bugs efficiently, and continuously raising product quality. I work across front-end and back-end, oriented toward modern architecture and engineering practices.",
  aboutP2:
    "I build modern, performant interfaces with Next.js, React, and Angular using TypeScript, and I have deep back-end experience with Node.js, NestJS, and Java (Spring Boot). I apply DDD for domain modeling, TDD/BDD to validate behavior, and SOLID to keep systems extensible, loosely coupled, and cohesive.",
  aboutP3:
    "I practice Clean Architecture, Hexagonal Architecture, and Clean Code: clear layering, business rules independent from implementation details, and ports and adapters for modularity.",
  principlesTitle: "How I build",
  experienceTitle: "Experience",
  experienceEyebrow: "Career",
  skillsTitle: "Stack",
  skillsEyebrow: "Tools",
  projectsTitle: "Projects",
  projectsEyebrow: "Selected",
  projectsIntro: "APIs, apps, and experiments that show the kind of work I like to ship.",
  educationTitle: "Education",
  educationEyebrow: "Foundation",
  degreeLabel: "Degree",
  degreeTitle: "Computer Science",
  degreeSchool: "UNIP",
  degreePeriod: "2018 — 2021",
  degreeDescription:
    "Bachelor's degree in Computer Science, with a foundation in algorithms, software engineering, and systems.",
  coursesTitle: "Courses",
  languagesTitle: "Languages",
  langEnglish: "English — advanced",
  langSpanish: "Spanish — basic",
  contactTitle: "Let's talk",
  contactEyebrow: "Contact",
  contactLead:
    "Open to remote or hybrid roles. If it looks like a fit, reach out.",
  contactEmail: "Email",
  contactWhatsapp: "WhatsApp",
  footer: "Campinas, Brazil",
  viewProject: "View on GitHub",
  viewCertificate: "View certificate",
  principles: [
    {
      title: "DDD",
      text: "Domain modeling aligned with the business, not just the stack.",
    },
    {
      title: "TDD / BDD",
      text: "Behavior validated early, with tests that protect evolution.",
    },
    {
      title: "SOLID",
      text: "Extensibility, low coupling, and high cohesion in day-to-day work.",
    },
    {
      title: "Clean / Hexagonal",
      text: "Business rules independent of frameworks, databases, and UI.",
    },
  ],
  experience: [
    {
      company: "NAVA",
      role: "Full-Stack Developer / Software Engineer",
      period: "Sep 2024 — present",
      description:
        "Backend engineering focused on Java, Apache Camel, and high-throughput integrations. Legacy migrations, performance work, architecture modeling, and evolution of critical applications using DDD, BDD, and SOLID.",
      tags: ["Java", "Apache Camel", "DDD", "BDD", "Integrations"],
    },
    {
      company: "Upwork",
      role: "Software Engineer",
      period: "Aug 2023 — Aug 2024",
      description:
        "Fintech solutions with React, TypeScript, Java, and Spring Boot, including payment systems and ERP modules. Owned new features, bug fixes, and service integrations.",
      tags: ["React", "TypeScript", "Java", "Spring Boot", "Fintech"],
    },
    {
      company: "Coopersystem",
      role: "Software Engineer",
      period: "Jan 2022 — Aug 2023",
      description:
        "Swift integration for React Native packages and end-to-end React Native apps, from concept to delivery. Also worked with Angular, React, TypeScript, Node.js, and AWS S3/EC2.",
      tags: ["React Native", "Swift", "Angular", "Node.js", "AWS"],
    },
    {
      company: "Qyon Intelligent Systems",
      role: "Software Engineer",
      period: "Apr 2021 — Nov 2021",
      description:
        "Led front-end development for a fintech app: React Native pages and components with TypeScript, technical meetings, and feature definition.",
      tags: ["React Native", "TypeScript", "Fintech"],
    },
    {
      company: "BUTH",
      role: "Junior Software Developer",
      period: "Sep 2020 — Apr 2021",
      description:
        "Screens and bug fixes with React and React Native, TypeScript, Bootstrap, and Styled Components. Back-end APIs and features with Node.js.",
      tags: ["React", "React Native", "Node.js"],
    },
    {
      company: "Matera Financial Risk Retail",
      role: "Intern",
      period: "Jan 2019 — Dec 2019",
      description:
        "Java Spring Boot on complex ERP systems: development and bug fixes, using Docker, Swagger, and OracleDB.",
      tags: ["Java", "Spring Boot", "Docker", "Oracle"],
    },
  ],
  skillGroups: [
    {
      title: "Front-end",
      items: ["React", "Next.js", "Angular", "TypeScript", "JavaScript"],
    },
    {
      title: "Back-end",
      items: ["Java", "Spring Boot", "Apache Camel", "Node.js", "NestJS"],
    },
    {
      title: "Mobile",
      items: ["React Native", "Swift"],
    },
    {
      title: "Architecture",
      items: ["DDD", "TDD / BDD", "SOLID", "Clean Architecture", "Hexagonal"],
    },
    {
      title: "Cloud and data",
      items: ["AWS S3", "EC2", "Docker", "MySQL", "MongoDB", "OracleDB", "Swagger"],
    },
  ],
  projects: {
    petshop: "Petshop REST API with NestJS, TypeORM, and MySQL.",
    checkIns: "Check-in API in Java Spring Boot, using Lombok and clean API practices.",
    goBarber: "Barbershop platform with React, React Native, and Node.",
    gitHunter: "GitHub profile search by ID, built with React.",
    mostUsedWords: "Word-frequency tool for a given text, built with Vue.js.",
    habits: "Habit and commitment tracker in React.",
  },
  courses: [
    {
      title: "Java — Object-Oriented Programming",
      subtitle: "Feb 2023 — Aug 2023",
    },
    {
      title: "Complete Modern Web with JavaScript",
      subtitle: "Udemy · Aug 2021 — May 2022",
    },
    {
      title: "Swift App Development Bootcamp",
      subtitle: "Feb 2021 — Jul 2021",
    },
    {
      title: "Rocketseat GoStack",
      subtitle: "Jan 2020 — Nov 2020",
    },
    {
      title: "Python",
      subtitle: "Udemy · Mar 2019 — Apr 2019",
    },
  ],
};
